import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import Filter from "../modules/Filter";
import { getCardTypesWithoutPagination } from "layouts/card_type/services/cardTypeService";
import { getPerFilter, getPerFilterPerFile, getReportsPerFilter } from "../services/filterService";
import { v4 as uuidv4 } from "uuid";
import { getStatusTypes } from "layouts/card/services/cardService";
import { getSourceTypes } from "layouts/sources_data/services/dataSourceService";
import MDAlert from "components/Alert";
import { getRolesWithoutPagination } from "layouts/role/services/RoleService";

const FilterController = forwardRef(
  (
    {
      setListData,
      dataFilter,
      dataTable,
      setTotalPages,
      setCurrentPage,
      currentPage,
      requestData,
      setError = () => {},
      setTotalData,
      setEntriesEnd,
      setEntriesStart,
      dataSourceId,
      setLoading = () => {},
    },
    ref
  ) => {
    // Estados para datos de opciones de filtros
    const [dataTypeCard, setDataTypeCard] = useState();
    const [dataRoles, setDataRoles] = useState();
    const [dataSourceTypes, setDataSourceTypes] = useState();
    const [dataStatus, setDataStatus] = useState();
    const [dataAttendance, setDataAttendance] = useState();

    // Estados para valores de filtros
    const [dateFrom, setDateFrom] = useState("date");
    const [dateUntil, setDateUntil] = useState("date");
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("");
    const [filters, setFilters] = useState([]);

    // Estados para visibilidad de componentes
    const [isVisible, setIsVisible] = useState(false);
    const [isVisibleType, setIsVisibleType] = useState(false);
    const [isVisibleSelectDateInput, setIsVisibleSelectDateInput] = useState(false);

    // Estados para validación
    const [isFieldValid, setIsFieldValid] = useState({ search: true, date: true });

    // Estados para alertas
    const [isAlert, setIsAlert] = useState(false);
    const [typeAlert, setTypeAlert] = useState("");
    const [message, setMessage] = useState("");

    // Estados de configuración
    const [table] = useState(dataTable);
    const [paginaSize] = useState(10);
    // Constantes de tipos de filtros
    const FILTER_TYPES = {
      DATE: [
        "created",
        "created_at",
        "expires_in",
        "updated_at",
        "latest_link_created_at",
        "first_entry",
        "last_exit",
      ],
      NUMERIC: ["total_reads"],
    };

    // Ejecutar función requestCarnetsDate con el arreglo de filtros
    const searchValidation = (currentPage, pageSize) => {
      if (dataSourceId) {
        // Usar función específica para reportes de asistencia
        if (table === "reports_attendance") {
          requestReportsFilter(dataSourceId, currentPage, pageSize, filters);
        } else {
          requestFilterPerIdFile(dataSourceId, currentPage, pageSize, filters);
        }
      } else {
        requestFilter(currentPage, pageSize, filters);
      }
    };

    /**
     * Actualiza el estado con los datos de paginación recibidos
     * @param {Object} result - Resultado de la petición con datos de paginación
     * @param {number} pageIndex - Índice de la página actual
     */
    const updatePaginationState = (result, pageIndex) => {
      const { data, last_page, total, from, to } = result;
      setListData(data);
      setTotalPages(last_page);
      if (last_page <= pageIndex) {
        setCurrentPage(last_page);
      }
      setTotalData(total);
      setEntriesStart(from);
      setEntriesEnd(to);
      setLoading(false);
      setError("");
    };

    /**
     * Maneja errores de peticiones de filtros
     * @param {string} errorMessage - Mensaje de error a mostrar
     */
    const handleFilterError = (errorMessage = "Error al consultar al servidor") => {
      handleAlert("fail", errorMessage);
      setLoading(false);
      setError(errorMessage);
    };

    /**
     * Obtiene datos filtrados de la tabla principal
     * @param {number} pageIndex - Índice de la página
     * @param {number} pageSize - Tamaño de la página
     * @param {Array} filters - Filtros a aplicar
     */
    const requestFilter = async (pageIndex, pageSize, filters) => {
      try {
        const result = await getPerFilter(pageIndex, pageSize, filters, table);

        if (result && result.status !== false) {
          updatePaginationState(result, pageIndex);
        } else {
          handleFilterError(result.message);
        }
      } catch (error) {
        handleFilterError(error.message);
      }
    };

    /**
     * Obtiene datos filtrados por archivo o fuente de datos
     * @param {string|number} id - ID del archivo o fuente
     * @param {number} pageIndex - Índice de la página
     * @param {number} pageSize - Tamaño de la página
     * @param {Array} filters - Filtros a aplicar
     */
    const requestFilterPerIdFile = async (id, pageIndex, pageSize, filters) => {
      try {
        const result = await getPerFilterPerFile(id, pageIndex, pageSize, filters, dataTable);

        if (result.status !== false) {
          updatePaginationState(result, pageIndex);
        } else {
          handleFilterError(result.message);
        }
      } catch (error) {
        handleFilterError(error.message);
      }
    };

    /**
     * Obtiene reportes de asistencia filtrados
     * @param {string|number} id - ID del evento
     * @param {number} pageIndex - Índice de la página
     * @param {number} pageSize - Tamaño de la página
     * @param {Array} filters - Filtros a aplicar
     */
    const requestReportsFilter = async (id, pageIndex, pageSize, filters) => {
      try {
        const result = await getReportsPerFilter(id, pageIndex, pageSize, filters);

        if (result.status !== false) {
          updatePaginationState(result, pageIndex);
        } else {
          handleFilterError(result.message);
        }
      } catch (error) {
        handleFilterError(error.message);
      }
    };

    // Tipos de filtros por categoría
    const FILTER_CATEGORIES = {
      SELECT: [
        "type_carnet",
        "status",
        "joined",
        "source_type_name",
        "role_id",
        "blood_type",
        "notified",
        "latest_link_status",
        "card_type",
        "attended",
      ],
      TEXT: [
        "number_id",
        "name",
        "email",
        "user_email",
        "source_name",
        "file_name",
        "description",
        "total_reads_min",
        "total_reads_max",
      ],
      DATE: [
        "created",
        "created_at",
        "expires_in",
        "updated_at",
        "latest_link_created_at",
        "first_entry",
        "last_exit",
        "total_reads_range",
      ],
    };

    /**
     * Restablece todos los campos del formulario
     */
    const resetFormFields = () => {
      setIsVisible(false);
      setIsVisibleType(false);
      setIsVisibleSelectDateInput(false);
      setSearch("");
      setDateFrom("date");
      setDateUntil("date");
      resetFieldValid();
    };

    /**
     * Controla el comportamiento de los filtros según el tipo seleccionado
     * @param {Event} event - Evento del selector de filtros
     */
    const handleFilter = (event) => {
      const selectedFilter = event.target.value;
      setFilter(selectedFilter);

      if (!selectedFilter) {
        resetFormFields();
        return;
      }

      resetFormFields();

      if (FILTER_CATEGORIES.SELECT.includes(selectedFilter)) {
        setIsVisibleType(true);
      } else if (FILTER_CATEGORIES.TEXT.includes(selectedFilter)) {
        setIsVisible(true);
      } else if (FILTER_CATEGORIES.DATE.includes(selectedFilter)) {
        setIsVisibleSelectDateInput(true);
      }
    };

    /**
     * Maneja la adición de filtros al sistema
     * Si ya existe un filtro del mismo tipo, lo reemplaza; de lo contrario, agrega uno nuevo
     * Valida que los campos requeridos estén completos antes de agregar el filtro
     */
    const handleAddFilter = (event) => {
      // Determinar el tipo de filtro actual
      const isDateFilter = FILTER_TYPES.DATE.includes(filter);
      const isNumericFilter = FILTER_TYPES.NUMERIC.includes(filter);
      const isTextFilter = !isDateFilter && !isNumericFilter;

      // Validar campos requeridos según el tipo de filtro
      if (!validateFilterInputs(isDateFilter, isNumericFilter, isTextFilter)) {
        return;
      }

      // Crear el objeto del filtro
      const newFilter = createFilterObject();
      if (!newFilter) return;

      // Buscar si ya existe un filtro del mismo tipo
      const existingFilterIndex = filters.findIndex((item) => item.type === filter);

      if (existingFilterIndex !== -1) {
        // Reemplazar filtro existente
        const updatedFilters = [...filters];
        updatedFilters[existingFilterIndex] = newFilter;
        setFilters(updatedFilters);
      } else {
        // Agregar nuevo filtro
        setFilters([...filters, newFilter]);
      }

      // Restablecer el estado del formulario
      resetFilterState();
    };

    /**
     * Valida que los campos de entrada del filtro estén completos
     * @param {boolean} isDateFilter - Indica si es un filtro de fecha
     * @param {boolean} isNumericFilter - Indica si es un filtro numérico
     * @param {boolean} isTextFilter - Indica si es un filtro de texto
     * @returns {boolean} - true si la validación es exitosa, false en caso contrario
     */
    const validateFilterInputs = (isDateFilter, isNumericFilter, isTextFilter) => {
      // Validar filtros de fecha y numéricos (requieren dateFrom y dateUntil)
      if ((isDateFilter || isNumericFilter) && (dateFrom === "date" || dateUntil === "date")) {
        handleChangeValid("date", false);
        return false;
      }

      // Validar filtros de texto (requieren campo search)
      if (isTextFilter) {
        const isSearchEmpty =
          (!search && search !== 0 && search !== "0") ||
          (typeof search === "string" && !search.trim()) ||
          search === "";

        if (isSearchEmpty) {
          handleChangeValid("search", false);
          return false;
        }
      }

      return true;
    };

    /**
     * Crea el objeto del filtro con su tipo, valor y etiqueta
     * @returns {Object|null} - Objeto del filtro o null si no se puede crear
     */
    const createFilterObject = () => {
      const filterLabel = getFilterLabel();
      if (!filterLabel) return null;

      const filterValue = getFilterValue();

      return {
        id: uuidv4(),
        type: filter,
        value: filterValue,
        label: filterLabel,
      };
    };

    /**
     * Obtiene la etiqueta descriptiva del filtro según su tipo
     * @returns {string|null} - Etiqueta del filtro o null si no es válido
     */
    const getFilterLabel = () => {
      const searchValue = typeof search === "string" ? search : String(search);
      const trimmedSearch = typeof search === "string" ? search.trim() : searchValue;

      switch (filter) {
        case "type_carnet":
        case "card_type":
          return `Tipo de Carnet: ${trimmedSearch}`;

        case "source_type_name":
          return `Tipo de Fuente: ${trimmedSearch}`;

        case "role_id":
          return `Tipo de rol: ${trimmedSearch}`;

        case "blood_type":
          return `Tipo de sangre: ${trimmedSearch}`;

        case "notified":
          return `Estado de notificación: ${
            trimmedSearch === "0" ? "No Notificados" : "Notificados"
          }`;

        case "latest_link_status":
          return `Estado de descarga: ${trimmedSearch === "0" ? "Pendientes" : "Descargados"}`;

        case "joined":
          return `Estado de ingreso: ${trimmedSearch === "0" ? "Pendientes" : "Ingresados"}`;

        case "attended":
          if (search === "" || search === null || search === undefined) return null;
          const attendedValue =
            searchValue === "1" || searchValue === "true" || searchValue === "Asistieron"
              ? "Asistieron"
              : "No Asistieron";
          return `Asistencia: ${attendedValue}`;

        case "number_id":
          if (!trimmedSearch) return null;
          return `Documento de Identificación: ${trimmedSearch}`;

        case "name":
          if (!trimmedSearch) return null;
          const nameLabel =
            table !== "roles" && table !== "card_types" ? "Nombre o Apellido" : "Nombre";
          return `${nameLabel}: ${trimmedSearch}`;

        case "source_name":
        case "file_name":
          if (!trimmedSearch) return null;
          return `Nombre: ${trimmedSearch}`;

        case "description":
          if (!trimmedSearch) return null;
          return `Descripción: ${trimmedSearch}`;

        case "total_reads_min":
          if (!trimmedSearch) return null;
          return `Total Lecturas Mínimo: ${trimmedSearch}`;

        case "total_reads_max":
          if (!trimmedSearch) return null;
          return `Total Lecturas Máximo: ${trimmedSearch}`;

        case "total_reads":
          if (dateFrom === "date" || dateUntil === "date") return null;
          return `Total de Lecturas: ${dateFrom} - ${dateUntil}`;

        case "email":
        case "user_email":
          if (!trimmedSearch) return null;
          return `Correo Electrónico: ${trimmedSearch}`;

        case "status":
          if (!trimmedSearch) return null;
          return `ESTADO: ${trimmedSearch}`;

        case "created":
        case "created_at":
          if (dateFrom === "date" || dateUntil === "date") return null;
          return `Fecha Generación: ${dateFrom} - ${dateUntil}`;

        case "latest_link_created_at":
          if (dateFrom === "date" || dateUntil === "date") return null;
          return `Fecha ultima notificación: ${dateFrom} - ${dateUntil}`;

        case "expires_in":
          if (dateFrom === "date" || dateUntil === "date") return null;
          return `Fecha Vigencia: ${dateFrom} - ${dateUntil}`;

        case "updated_at":
          if (dateFrom === "date" || dateUntil === "date") return null;
          return `Fecha de Actualizacion: ${dateFrom} - ${dateUntil}`;

        case "first_entry":
          if (dateFrom === "date" || dateUntil === "date") return null;
          return `Primera Entrada: ${dateFrom} - ${dateUntil}`;

        case "last_exit":
          if (dateFrom === "date" || dateUntil === "date") return null;
          return `Última Salida: ${dateFrom} - ${dateUntil}`;

        default:
          return null;
      }
    };

    /**
     * Obtiene el valor del filtro según su tipo
     * @returns {Object|string} - Valor del filtro (objeto con from/until para fechas, string para otros)
     */
    const getFilterValue = () => {
      if (FILTER_TYPES.DATE.includes(filter)) {
        return { from: dateFrom, until: dateUntil };
      }

      return typeof search === "string" ? search.trim() : String(search);
    };

    /**
     * Restablece todos los campos del formulario de filtros a su estado inicial
     */
    const resetFilterState = () => {
      setFilter("");
      setIsVisible(false);
      setIsVisibleType(false);
      setIsVisibleSelectDateInput(false);
      setSearch("");
      setDateFrom("date");
      setDateUntil("date");
    };

    /**
     * Elimina un filtro del arreglo de filtros activos
     * @param {string} id - ID del filtro a eliminar
     */
    const handleDeleteFilter = (id) => {
      setFilters((prevFilters) => prevFilters.filter((item) => item.id !== id));
    };

    // Hace llamado a la funcion que realiza la peticion de los tipos de carnets y status cuando se renderiza el componente
    useEffect(() => {
      setLoading(true);
      // Itera sobre cada elemento del array
      dataFilter[0].forEach((filterObj) => {
        switch (filterObj.value) {
          case "type_carnet":
          case "card_type":
            requestCardType();
            break;
          case "status":
            requestStatus();
            break;
          case "source_type_name":
            requestSourceTypes();
            break;
          case "role_id":
            requestGetRoles();
            break;
          case "attended":
            requestAttendanceOptions();
            break;
          default:
            break;
        }
      });
    }, []);

    // Hace llamado a la funcion que valida la data con los filtros actuales cuando la pagina actual cambia
    useEffect(() => {
      searchValidation(currentPage, paginaSize);
    }, [currentPage, filters]);

    /**
     * Obtiene los tipos de carnet disponibles
     */
    const requestCardType = async () => {
      const result = await getCardTypesWithoutPagination();
      if (result?.status !== false) {
        setDataTypeCard(result.payload);
      }
    };

    /**
     * Obtiene los roles disponibles
     */
    const requestGetRoles = async () => {
      const result = await getRolesWithoutPagination();
      if (result?.status !== false) {
        setDataRoles(result.payload);
      }
    };

    /**
     * Obtiene los tipos de fuente de datos disponibles
     */
    const requestSourceTypes = async () => {
      const result = await getSourceTypes();
      if (result?.status !== false) {
        setDataSourceTypes(result.payload);
      }
    };

    /**
     * Obtiene los estados disponibles
     */
    const requestStatus = async () => {
      const result = await getStatusTypes();
      if (result?.status !== false) {
        setDataStatus(result.data);
      }
    };

    /**
     * Crea las opciones para el filtro de asistencia
     */
    const requestAttendanceOptions = () => {
      setDataAttendance([
        { id: 1, name: "Asistieron" },
        { id: 0, name: "No Asistieron" },
      ]);
    };

    /**
     * Maneja el cambio en los selectores de tipo de filtro
     * @param {Event} event - Evento del selector
     */
    const handleFilterType = (event) => {
      handleChangeValid("search", true);
      setSearch(event.target.value);
    };

    /**
     * Maneja el cambio en el campo de búsqueda de texto
     * @param {Event} event - Evento del input
     */
    const handleChange = (event) => {
      event.preventDefault();
      const value = event.target.value;

      // No permitir espacios al inicio
      if (value.startsWith(" ")) return;

      handleChangeValid("search", true);
      setSearch(value);
    };

    /**
     * Formatea una fecha a formato ISO (YYYY-MM-DD)
     * @param {string} dateValue - Valor de fecha del input
     * @returns {string} - Fecha formateada
     */
    const formatDateToISO = (dateValue) => {
      return new Date(dateValue).toISOString().slice(0, 10);
    };

    /**
     * Maneja el cambio de la fecha "desde"
     * Asegura que no sea mayor a la fecha "hasta"
     * @param {Event} event - Evento del input de fecha
     */
    const handleChangeDateFrom = (event) => {
      handleChangeValid("date", true);

      if (!event.target.value) {
        setDateFrom("date");
        return;
      }

      let formattedDate = formatDateToISO(event.target.value);

      // Validar que no sea mayor a dateUntil
      if (dateUntil !== "" && dateUntil !== "date" && formattedDate > dateUntil) {
        formattedDate = dateUntil;
      }

      setDateFrom(formattedDate);
    };

    /**
     * Maneja el cambio de la fecha "hasta"
     * Asegura que no sea menor a la fecha "desde"
     * @param {Event} event - Evento del input de fecha
     */
    const handleChangeDateUntil = (event) => {
      handleChangeValid("date", true);

      if (!event.target.value) {
        setDateUntil("date");
        return;
      }

      let formattedDate = formatDateToISO(event.target.value);

      // Validar que no sea menor a dateFrom
      if (dateFrom !== "" && dateFrom !== "date" && formattedDate < dateFrom) {
        formattedDate = dateFrom;
      }

      setDateUntil(formattedDate);
    };

    /**
     * Maneja el envío de filtros (wrapper para handleAddFilter)
     */
    const handleSubmitFilters = () => {
      handleAddFilter();
    };

    /**
     * Ejecuta la búsqueda con los filtros actuales
     */
    const handleClickSubmit = () => {
      searchValidation(currentPage, paginaSize);
    };

    /**
     * Cambia el estado de validación de un campo
     * @param {string} fieldName - Nombre del campo a validar
     * @param {boolean} isValid - Estado de validación
     */
    const handleChangeValid = (fieldName, isValid) => {
      setIsFieldValid((prevValidity) => ({
        ...prevValidity,
        [fieldName]: isValid,
      }));
    };

    /**
     * Restablece todas las validaciones a su estado inicial
     */
    const resetFieldValid = () => {
      setIsFieldValid({ search: true, date: true });
    };

    /**
     * Muestra una alerta al usuario
     * @param {string} typeAlert - Tipo de alerta (success, fail, etc.)
     * @param {string} message - Mensaje a mostrar
     */
    const handleAlert = (typeAlert, message) => {
      setTypeAlert(typeAlert);
      setMessage(message);
      setIsAlert(true);
    };

    // Exponer handleClickSubmit al componente padre
    useImperativeHandle(ref, () => ({
      handleClickSubmit,
    }));

    return (
      <>
        <MDAlert
          isAlert={isAlert}
          setIsAlert={setIsAlert}
          typeAlert={typeAlert}
          message={message}
        />
        <Filter
          dataFilter={dataFilter}
          filter={filter}
          handleFilter={handleFilter}
          isVisible={isVisible}
          handleChange={handleChange}
          search={search}
          handleFilterType={handleFilterType}
          isVisibleType={isVisibleType}
          isVisibleSelectDateInput={isVisibleSelectDateInput}
          dateFrom={dateFrom}
          dateUntil={dateUntil}
          handleChangeDateFrom={handleChangeDateFrom}
          handleChangeDateUntil={handleChangeDateUntil}
          handleClickSubmit={handleSubmitFilters}
          dataRoles={dataRoles}
          dataTypeCard={dataTypeCard}
          dataStatus={dataStatus}
          dataSourceTypes={dataSourceTypes}
          dataAttendance={dataAttendance}
          handleAddFilter={handleAddFilter}
          filters={filters}
          handleDeleteFilter={handleDeleteFilter}
          isFieldValid={isFieldValid}
        />
      </>
    );
  }
);
export default FilterController;
